import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  StopIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  HomeIcon,
  ArrowPathIcon,
  TrashIcon,
  PlusIcon,
} from '@heroicons/react/24/solid';
import { PanelCard, Section, Notice, inputCls } from './controls';

/**
 * Velocity for every continuous move. There is no speed control on the panel: the pad and the
 * zoom buttons are press-and-hold, so how far the head travels is already set by how long the
 * button is held — a second dial for the same thing only made the panel harder to read.
 */
const MOVE_SPEED = 0.5;

/**
 * A tap on a direction or zoom button still has to move the head enough to see. Below roughly
 * this long the camera we measured does not travel far enough to change its reported position at
 * all, so a quick click looked like it had done nothing. The stop is held back to this floor,
 * which keeps one code path — a click is simply the shortest possible hold.
 */
const MIN_PULSE_MS = 700;
/**
 * The same floor for a zoom tap on a camera whose lens position is read from the lens itself
 * (`zoom_source: "lens"`, the CP Plus motorised bullets). Those lenses do move on a short tap —
 * measured 150ms ≈ 10%, 300ms ≈ 16%, 700ms ≈ 28% — and their motor has one fixed speed, so the
 * 700ms floor turned every tap into a 28% jump and fine framing took many corrective presses.
 */
const LENS_ZOOM_PULSE_MS = 200;
/**
 * While a button is held, poll the position this often. Cheap (one SOAP call, ~30ms) and safe
 * during a continuous move — unlike during an absolute one, which is why only the settle path
 * below is gated. Cameras that publish their position live then read live; ones that freeze it
 * until the move ends simply show no change until the settle reads land.
 */
const LIVE_POLL_MS = 500;
/**
 * When to take the first reading after a stop. The camera measured here publishes its new position
 * roughly 800ms AFTER the stop, so an earlier read just returns the old number.
 */
const FIRST_READ_MS = 900;
/**
 * A camera reports MoveStatus while it is still finishing, and the one here holds "MOVING" for
 * several seconds after a short nudge — during which it silently ignores any new move. That is
 * what made repeated presses look like they did nothing, so the position is re-read on this
 * interval until it goes idle and the controls can honestly be offered again.
 */
const IDLE_POLL_MS = 1200;
/**
 * Stop watching eventually: a camera that never reports idle must not disable its own controls.
 * Generous, because a long absolute travel on the camera measured here took ~12s to report idle,
 * and giving up early would re-enable the buttons while it still discards what they send.
 */
const IDLE_WATCH_MS = 20000;
/** An absolute move travels after the request returns, and must not be polled while it does. */
const ABSOLUTE_SETTLE_MS = 3000;

/** One line of the live position readout: angle/percentage plus a bar for the same value. */
function PositionRow({ label, percent, detail }: any) {
  const clamped = Math.max(0, Math.min(100, percent ?? 0));
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[12px]">
        <span className="font-medium text-foreground">{label}</span>
        <span className="tabular-nums text-muted-foreground">{detail}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}

function formatAngle(degrees?: number | null, percent?: number | null, exact?: boolean): string {
  if (degrees == null) return percent == null ? '—' : `${percent}%`;
  return `${exact ? '' : '≈'}${degrees}°${percent == null ? '' : ` · ${percent}%`}`;
}

/** Absolute-position slider: commits on release, including for keyboard users. */
function AbsoluteSlider({ label, value, min, max, disabled, onDrag, onCommit }: any) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-[12px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular-nums text-muted-foreground">{Number(value).toFixed(2)}</span>
      </div>
      <input
        type="range" disabled={disabled} min={min} max={max} step={0.01} value={value}
        aria-label={`${label} position`}
        onChange={(e) => onDrag(Number(e.target.value))}
        onPointerUp={(e) => onCommit(Number((e.currentTarget as HTMLInputElement).value))}
        onKeyUp={(e) => onCommit(Number((e.currentTarget as HTMLInputElement).value))}
        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-brand disabled:opacity-40"
      />
    </div>
  );
}

export default function PTZPanel({ onPTZ, presets = [], loading, ptzCapabilities = {}, status, onRefreshStatus }: any) {
  const panTilt = ptzCapabilities.pan_tilt_supported;
  const zoomOk = ptzCapabilities.zoom_supported;
  const absoluteOk = ptzCapabilities.absolute_move_supported;
  const homeOk = ptzCapabilities.home_supported;
  const [presetName, setPresetName] = useState('');
  const [absolute, setAbsolute] = useState<{ pan: number; tilt: number; zoom: number }>({ pan: 0, tilt: 0, zoom: 0 });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  // Zoom as a percentage, so the bar can be dragged without waiting for the camera to answer.
  const [zoomPct, setZoomPct] = useState(0);
  // A status read landing mid-drag would otherwise yank the thumb out from under the finger.
  const zoomDragging = useRef(false);
  // True while the camera reports it is still completing a zoom, so the controls can wait with it.
  const [zoomFinishing, setZoomFinishing] = useState(false);
  /**
   * The level the user last commanded, held as the displayed value once set.
   *
   * Measured on a Matrix zoom-only camera: it ACCEPTS an absolute position on the 0–1 scale it
   * advertises, but REPORTS its position back scaled by a constant ~0.388 (command 0.5, read
   * 0.194; command 0.8, read 0.310). Its own web page shows the commanded scale, so adopting the
   * reading turned a level the user set to 14% into "5.4%" and disagreed with the camera in front
   * of them. Where we know what was asked for, that is the better answer; the reading is still
   * used for nudges, where there is no commanded target to prefer.
   */
  const commandedZoomPct = useRef<number | null>(null);

  // Keep the absolute sliders on the head's real position, so dragging one starts from where
  // the camera actually is instead of snapping it back to a stale value.
  useEffect(() => {
    if (!status) return;
    setAbsolute({
      pan: status.pan ?? 0,
      tilt: status.tilt ?? 0,
      zoom: status.zoom ?? 0,
    });
    if (status.zoom_percent != null && !zoomDragging.current && commandedZoomPct.current == null) {
      setZoomPct(status.zoom_percent);
    }
    // The camera's own account of whether it has finished. Taken at face value: while it says it
    // is moving it discards new commands, so offering them would be a lie the user pays for.
    const move = String(status.move_status?.zoom ?? '').toUpperCase();
    setZoomFinishing(!!move && move !== 'IDLE');
  }, [status]);

  /**
   * A continuous move runs until it is told to stop, so the stop must survive the pointer
   * being released anywhere — off the button, outside the window, or on a touch screen that
   * cancels the gesture. Releasing is tracked globally rather than per-button, which is the
   * difference between a dome that stops and a dome that keeps panning into the wall.
   */
  const moving = useRef(false);

  // One status read at a time — they are serialised on the camera anyway, so overlapping them
  // only queues work in front of the command the user is waiting on.
  const polling = useRef(false);
  // Set only while an ABSOLUTE move is travelling. Reads are suppressed for that window because
  // this is the one case that hurts: asked for its position mid-absolute-move, the camera stops
  // answering entirely. A continuous move takes the same question happily.
  const settling = useRef(false);
  const refresh = useCallback(async () => {
    if (polling.current || settling.current) return null;
    polling.current = true;
    try {
      return await onRefreshStatus?.();
    } finally {
      polling.current = false;
    }
  }, [onRefreshStatus]);

  // Live position while a button is held, for cameras that report it during a move.
  const liveTimer = useRef<any>(null);
  const startLiveReads = useCallback(() => {
    clearInterval(liveTimer.current);
    liveTimer.current = setInterval(refresh, LIVE_POLL_MS);
  }, [refresh]);
  const stopLiveReads = useCallback(() => {
    clearInterval(liveTimer.current);
    liveTimer.current = null;
  }, []);

  /**
   * Follow the head until it reports itself idle, so the readout catches up and the controls come
   * back exactly when the camera will actually accept the next command.
   */
  const idleWatch = useRef<any>(null);
  const watchUntilIdle = useCallback(() => {
    clearTimeout(idleWatch.current);
    const startedAt = Date.now();
    const tick = async () => {
      const s = await refresh();
      const move = String(s?.move_status?.zoom ?? s?.move_status?.pan_tilt ?? '').toUpperCase();
      const stillMoving = !!move && move !== 'IDLE';
      if (stillMoving && Date.now() - startedAt < IDLE_WATCH_MS) {
        idleWatch.current = setTimeout(tick, IDLE_POLL_MS);
      } else if (stillMoving) {
        // Gave up waiting. A camera that never admits to being idle must not be allowed to hold
        // its own controls shut — that is the difference between "finishing…" and a dead panel.
        setZoomFinishing(false);
      }
    };
    idleWatch.current = setTimeout(tick, FIRST_READ_MS);
  }, [refresh]);

  /**
   * Read back after an absolute move: reads are held off until the head should have arrived, then
   * followed until it settles. A single read here was landing mid-travel and leaving that
   * half-way figure on screen as though it were the final position.
   */
  const refreshAfter = useCallback((delayMs: number) => {
    settling.current = true;
    setTimeout(() => {
      settling.current = false;
      watchUntilIdle();
    }, delayMs);
  }, [watchUntilIdle]);

  // When the current press began, so a tap can be held open to its floor.
  const pressedAt = useRef(0);
  const pulseFloor = useRef(MIN_PULSE_MS);

  const hardStop = useCallback(async () => {
    moving.current = false;
    stopLiveReads();
    // A tap is the shortest hold rather than a separate gesture: keep the move running until it
    // has covered the floor, otherwise the head never travels far enough to see and the button
    // reads as broken.
    const remaining = pulseFloor.current - (Date.now() - pressedAt.current);
    if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
    await onPTZ({ action: 'stop' });
    watchUntilIdle();
  }, [onPTZ, stopLiveReads, watchUntilIdle]);

  const endMove = useCallback(() => { if (moving.current) hardStop(); }, [hardStop]);

  useEffect(() => {
    window.addEventListener('pointerup', endMove);
    window.addEventListener('pointercancel', endMove);
    window.addEventListener('blur', endMove);
    return () => {
      window.removeEventListener('pointerup', endMove);
      window.removeEventListener('pointercancel', endMove);
      window.removeEventListener('blur', endMove);
    };
  }, [endMove]);

  // Stop on unmount too — navigating away mid-move otherwise leaves the head running.
  useEffect(() => () => {
    clearInterval(liveTimer.current);
    clearTimeout(idleWatch.current);
    if (moving.current) onPTZ({ action: 'stop' });
  }, [onPTZ]);

  const startMove = useCallback((panDir: number, tiltDir: number) => {
    moving.current = true;
    pressedAt.current = Date.now();
    pulseFloor.current = MIN_PULSE_MS;
    startLiveReads();
    return onPTZ({ action: 'move', pan: panDir * MOVE_SPEED, tilt: tiltDir * MOVE_SPEED });
  }, [onPTZ, startLiveReads]);

  const zoom = useCallback((dir: number) => {
    moving.current = true;
    pressedAt.current = Date.now();
    pulseFloor.current = status?.zoom_source === 'lens' ? LENS_ZOOM_PULSE_MS : MIN_PULSE_MS;
    // A nudge has no target of its own, so go back to reporting whatever the camera says.
    commandedZoomPct.current = null;
    startLiveReads();
    // Pan and tilt are left out entirely on a zoom-only camera: ONVIF makes the axis optional for
    // exactly that reason, and sending one the camera does not have is not politely ignored.
    return onPTZ({
      action: 'move',
      ...(panTilt ? { pan: 0, tilt: 0 } : {}),
      zoom: dir * MOVE_SPEED,
    });
  }, [onPTZ, panTilt, startLiveReads, status?.zoom_source]);

  const gotoPreset = useCallback(async (token: string) => {
    await onPTZ({ action: 'goto_preset', preset_token: token });
    refreshAfter(ABSOLUTE_SETTLE_MS);
  }, [onPTZ, refreshAfter]);

  const savePreset = useCallback(async () => {
    await onPTZ({ action: 'set_preset', preset_name: presetName || undefined });
    setPresetName('');
  }, [onPTZ, presetName]);

  const removePreset = useCallback(async (token: string) => {
    setConfirmDelete(null);
    await onPTZ({ action: 'remove_preset', preset_token: token });
  }, [onPTZ]);

  const moveAbsolute = useCallback(async (next: { pan: number; tilt: number; zoom: number }) => {
    setAbsolute(next);
    await onPTZ({
      action: 'absolute',
      // Same rule as a continuous move: only the axes this camera actually has.
      ...(panTilt ? { pan: next.pan, tilt: next.tilt } : {}),
      ...(zoomOk ? { zoom: next.zoom } : {}),
    });
    refreshAfter(ABSOLUTE_SETTLE_MS);
  }, [onPTZ, refreshAfter, panTilt, zoomOk]);

  // A zoom level can only be commanded when the camera published an absolute zoom space; the
  // percentage the UI shows is measured against that same range, so the two always agree.
  const zoomDirect = !!(zoomOk && absoluteOk && status?.zoom_range);

  // Where the lens actually is, as far as the camera will say. Without this there is no basis for
  // claiming a limit has been reached, so the buttons stay enabled rather than guessing.
  const zoomPctKnown = status?.zoom_percent != null || zoomDirect;
  // Half a percent of slack: the camera answers to one decimal and rarely lands on exactly 0 or
  // 100, so an equality test would leave the button live at the end of its travel.
  // Only bar the controls once the press has ended: disabling the button under a finger that is
  // still holding it would strand the move.
  const zoomWaiting = zoomFinishing && !moving.current;
  const atWidest = zoomPctKnown && zoomPct <= 0.5;
  const atTightest = zoomPctKnown && zoomPct >= 99.5;

  /** Send the head to a zoom percentage, converted back into the camera's own coordinates. */
  const commitZoomPct = useCallback((pct: number) => {
    zoomDragging.current = false;
    if (!zoomDirect) return;
    const clamped = Math.max(0, Math.min(100, pct));
    const { min = 0, max = 1 } = status.zoom_range || {};
    const raw = Number(min) + (clamped / 100) * (Number(max) - Number(min));
    commandedZoomPct.current = clamped;
    setZoomPct(clamped);
    moveAbsolute({ ...absolute, zoom: raw });
  }, [zoomDirect, status, absolute, moveAbsolute]);

  /** Arrow keys drive the head while the pad is focused; hold to keep moving, release to stop. */
  const onPadKeyDown = (e: React.KeyboardEvent) => {
    if (e.repeat || loading) return;
    const map: Record<string, [number, number]> = {
      ArrowUp: [0, 1], ArrowDown: [0, -1], ArrowLeft: [-1, 0], ArrowRight: [1, 0],
    };
    if (map[e.key]) { e.preventDefault(); startMove(map[e.key][0], map[e.key][1]); return; }
    if (zoomOk && (e.key === '+' || e.key === '=')) { e.preventDefault(); zoom(1); }
    if (zoomOk && (e.key === '-' || e.key === '_')) { e.preventDefault(); zoom(-1); }
  };

  const onPadKeyUp = (e: React.KeyboardEvent) => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', '+', '=', '-', '_'].includes(e.key)) endMove();
  };

  const dirBtn = 'flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-card text-foreground shadow-sm transition-all hover:bg-muted hover:shadow-md active:scale-95 active:bg-brand active:text-white disabled:opacity-40 disabled:pointer-events-none touch-none select-none';
  const stopBtn = 'flex h-12 w-12 items-center justify-center rounded-lg bg-destructive text-destructive-foreground shadow-sm transition-all hover:opacity-90 active:scale-95 disabled:opacity-40';
  const smallBtn = 'flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-[12px] font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-40 touch-none select-none';

  return (
    <PanelCard
      title="PTZ Control"
      description="Click and hold a direction, or focus the pad and hold an arrow key."
      actions={
        <button type="button" onClick={refresh} disabled={loading} className={smallBtn}>
          <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Position
        </button>
      }
    >
      {/* Current position */}
      <div className="pb-1">
        {status ? (
          <div className="space-y-3 rounded-lg border border-border bg-muted px-4 py-3">
            <div className="flex items-center justify-between">
              <p className="text-[12px] font-semibold text-foreground">Current Position</p>
              {status.move_status && (
                <span className="rounded-full bg-card px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {status.move_status.pan_tilt || status.move_status.zoom || ''}
                </span>
              )}
            </div>
            {panTilt && (
              <>
                <PositionRow label="Pan" percent={status.pan_percent}
                  detail={formatAngle(status.pan_degrees, status.pan_percent, status.angles_exact)} />
                <PositionRow label="Tilt" percent={status.tilt_percent}
                  detail={formatAngle(status.tilt_degrees, status.tilt_percent, status.angles_exact)} />
              </>
            )}
            {/* Zoom is not listed here — it lives with its own buttons under Drive, so there is
                one place that both shows the level and changes it. */}
            {!status.angles_exact && panTilt && (
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                This camera reports normalised coordinates, so angles are derived from its full
                travel range rather than read directly.
              </p>
            )}
            {status.error && <p className="text-[11px] text-destructive">{status.error}</p>}
          </div>
        ) : (
          <Notice>This camera does not report its PTZ position.</Notice>
        )}
      </div>

      {/* Drive — pad on the left, the controls it needs alongside it rather than stacked below. */}
      <Section title="Drive" collapsible={false}>
        <div className="grid items-start gap-5 sm:grid-cols-[auto_1fr]">
        {panTilt ? (
          <div
            tabIndex={0} role="group" aria-label="Pan and tilt control"
            onKeyDown={onPadKeyDown} onKeyUp={onPadKeyUp}
            className="flex flex-col items-center gap-1.5 rounded-xl py-1 focus:outline-none focus:ring-2 focus:ring-brand-ring"
          >
            <button type="button" className={dirBtn} disabled={loading} aria-label="Tilt up"
              onPointerDown={() => startMove(0, 1)}>
              <ArrowUpIcon className="h-5 w-5" />
            </button>
            <div className="flex gap-1.5">
              <button type="button" className={dirBtn} disabled={loading} aria-label="Pan left"
                onPointerDown={() => startMove(-1, 0)}>
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <button type="button" className={stopBtn} disabled={loading} aria-label="Stop"
                onClick={hardStop}>
                <StopIcon className="h-5 w-5" />
              </button>
              <button type="button" className={dirBtn} disabled={loading} aria-label="Pan right"
                onPointerDown={() => startMove(1, 0)}>
                <ArrowRightIcon className="h-5 w-5" />
              </button>
            </div>
            <button type="button" className={dirBtn} disabled={loading} aria-label="Tilt down"
              onPointerDown={() => startMove(0, -1)}>
              <ArrowDownIcon className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <Notice>Pan / tilt is not supported on this camera.</Notice>
        )}

        <div className="space-y-4">
        {/* Zoom — one box: the level, a bar to set it directly, and the hold-to-nudge buttons. */}
        {zoomOk ? (
          <div className="max-w-sm space-y-2.5 rounded-lg border border-border bg-muted px-3.5 py-3">
            <div className="flex items-baseline justify-between">
              <span className="text-[12px] font-medium text-foreground">Zoom</span>
              {/* One decimal, because that is the precision the camera reports: rounding 14.7 to
                  15 made the number look wrong next to a bar drawn from the real value. */}
              <span className="flex items-baseline gap-2">
                {zoomWaiting && (
                  <span className="text-[11px] font-normal text-muted-foreground">finishing…</span>
                )}
                <span className="text-[15px] font-semibold tabular-nums text-foreground">
                  {zoomPctKnown ? `${(Math.round(zoomPct * 10) / 10).toFixed(1)}%` : '—'}
                </span>
              </span>
            </div>

            {/* Draggable when the camera accepts an absolute zoom position; otherwise the same
                bar, read-only, so the level still reads the same way. */}
            {zoomDirect ? (
              <input
                type="range" min={0} max={100} step={1} value={zoomPct}
                disabled={loading || zoomWaiting}
                aria-label="Zoom level percent"
                onChange={(e) => { zoomDragging.current = true; setZoomPct(Number(e.target.value)); }}
                onPointerUp={(e) => commitZoomPct(Number((e.currentTarget as HTMLInputElement).value))}
                onKeyUp={(e) => commitZoomPct(Number((e.currentTarget as HTMLInputElement).value))}
                className="h-2 w-full cursor-pointer appearance-none rounded-full bg-card accent-brand disabled:opacity-40"
              />
            ) : (
              <div className="h-2 w-full overflow-hidden rounded-full bg-card">
                <div className="h-full rounded-full bg-brand transition-all"
                  style={{ width: `${Math.max(0, Math.min(100, zoomPct))}%` }} />
              </div>
            )}
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>Wide</span>
              <span>Tight</span>
            </div>

            {/* Each direction goes dead at its own end of the travel, so the button stops
                inviting a press the lens cannot answer. */}
            <div className="flex gap-2">
              <button type="button" className={`${smallBtn} flex-1 justify-center`}
                disabled={loading || atWidest || zoomWaiting}
                title={atWidest ? 'Already fully zoomed out'
                  : zoomWaiting ? 'Waiting for the camera to finish the last zoom' : undefined}
                aria-label="Zoom out" onPointerDown={() => zoom(-1)}>
                <MagnifyingGlassMinusIcon className="h-4 w-4" /> Out
              </button>
              <button type="button" className={`${smallBtn} flex-1 justify-center`}
                disabled={loading || atTightest || zoomWaiting}
                title={atTightest ? 'Already fully zoomed in'
                  : zoomWaiting ? 'Waiting for the camera to finish the last zoom' : undefined}
                aria-label="Zoom in" onPointerDown={() => zoom(1)}>
                <MagnifyingGlassPlusIcon className="h-4 w-4" /> In
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {zoomDirect
                ? 'Drag the bar to a level, or tap Out / In to step — hold to keep zooming.'
                : 'Tap Out / In to step, or hold to keep zooming. This camera cannot be sent '
                  + 'to a zoom level directly.'}
            </p>
          </div>
        ) : (
          <Notice>Zoom is not supported on this camera.</Notice>
        )}

        {/* Home */}
        {homeOk && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className={smallBtn} disabled={loading}
              onClick={async () => { await onPTZ({ action: 'goto_home' }); refreshAfter(ABSOLUTE_SETTLE_MS); }}>
              <HomeIcon className="h-4 w-4" /> Go Home
            </button>
            <button type="button" className={smallBtn} disabled={loading}
              onClick={() => onPTZ({ action: 'set_home' })}>
              Set Current As Home
            </button>
          </div>
        )}
        </div>
        </div>
      </Section>

      {/* Absolute positioning — available only when the camera publishes position spaces.
          Pan and tilt only: a second zoom control here just competed with the Out/In buttons,
          in different units. The move still carries the current zoom so it is not reset. */}
      {absoluteOk && status && panTilt && (
        <Section title="Go To Position" collapsible defaultOpen={false}
          hint="Drive straight to a coordinate instead of nudging.">
          <AbsoluteSlider label="Pan" value={absolute.pan} disabled={loading}
            min={status.pan_range?.min ?? -1} max={status.pan_range?.max ?? 1}
            onDrag={(v: number) => setAbsolute({ ...absolute, pan: v })}
            onCommit={(v: number) => moveAbsolute({ ...absolute, pan: v })} />
          <AbsoluteSlider label="Tilt" value={absolute.tilt} disabled={loading}
            min={status.tilt_range?.min ?? -1} max={status.tilt_range?.max ?? 1}
            onDrag={(v: number) => setAbsolute({ ...absolute, tilt: v })}
            onCommit={(v: number) => moveAbsolute({ ...absolute, tilt: v })} />
        </Section>
      )}

      {/* Presets */}
      <Section title="Presets" collapsible={false} badge={presets.length || undefined}
        hint="Stored on the camera itself.">
        {presets.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {presets.map((p: any) => (
              <span key={p.token}
                className="flex items-center gap-1 rounded-lg border border-brand-ring bg-brand-light py-1.5 pl-3 pr-1.5 transition-colors hover:bg-brand-light">
                <button type="button" onClick={() => gotoPreset(p.token)} disabled={loading}
                  title="Move to this preset"
                  className="text-[12px] font-medium text-brand hover:underline disabled:opacity-40">
                  {p.name || `Preset ${p.token}`}
                </button>
                {confirmDelete === p.token ? (
                  <span className="flex items-center gap-1">
                    <button type="button" onClick={() => removePreset(p.token)} disabled={loading}
                      className="rounded bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-destructive-foreground">
                      Delete
                    </button>
                    <button type="button" onClick={() => setConfirmDelete(null)}
                      className="px-1 text-[10px] font-medium text-muted-foreground hover:text-foreground">
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button type="button" onClick={() => setConfirmDelete(p.token)} disabled={loading}
                    title="Delete preset"
                    className="text-muted-foreground transition-colors hover:text-destructive disabled:opacity-40">
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-muted-foreground">No presets stored on this camera.</p>
        )}
        <div className="flex gap-2 pt-1">
          <input value={presetName} onChange={(e) => setPresetName(e.target.value)} disabled={loading}
            onKeyDown={(e) => { if (e.key === 'Enter') savePreset(); }}
            placeholder="Name this position" className={inputCls} />
          <button type="button" onClick={savePreset} disabled={loading}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-brand px-4 py-2 text-[12px] font-medium text-white transition-colors hover:bg-brand-dark disabled:opacity-40">
            <PlusIcon className="h-3.5 w-3.5" /> Save Current
          </button>
        </div>
      </Section>
    </PanelCard>
  );
}
